import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import logo from "@/assets/logo.png";
import { BookingPopupModal } from "@/components/home/BookingPopupModal";
import { TubingBookingPopupModal } from "@/components/home/TubingBookingPopupModal";
import { GlampingBookingPopupModal } from "@/components/home/GlampingBookingPopupModal";
import { BookNowMenuModal } from "@/components/home/BookNowMenuModal";
import { gtagReportConversion } from "@/lib/gtag";
const navItems = [
  { 
    name: "Day Rentals", 
    path: "/cabanas",
    dropdown: [
      { name: "Cabanas", path: "/cabanas" },
      { name: "Picnic Tables", path: "/cabanas#picnic-tables" },
    ]
  },
  { name: "Tubing", path: "/tubing" },
  { name: "Glamping", path: "/glamping" },
  { name: "Property Map", path: "/property-map" },
  { name: "FAQs", path: "/faqs" },
  { name: "Blog", path: "/blog" },
  { name: "Contact Us", path: "/contact" },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [bookNowMenuOpen, setBookNowMenuOpen] = useState(false);
  const [cabanaBookingOpen, setCabanaBookingOpen] = useState(false);
  const [tubingBookingOpen, setTubingBookingOpen] = useState(false);
  const [glampingBookingOpen, setGlampingBookingOpen] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  const handleNavClick = (path: string) => {
    if (path.includes('#')) {
      const [basePath, hash] = path.split('#');
      if (location.pathname === basePath) {
        // Already on the page, just scroll
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      if (window.scrollY > 50) {
        setBannerHidden(true);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          setBannerHidden(false);
        }, 3000);
      } else {
        setBannerHidden(false);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const isBookingPage = location.pathname.startsWith('/book-now');

  return (
    <>
      {/* Promo Banner - Desktop always visible, Mobile/Tablet with scroll behavior - Hidden on booking pages */}
      {!isBookingPage && (
        <a
          href="https://sonsriverranch.com/book-now"
          target="_blank"
          rel="noopener noreferrer"
          className={`fixed top-0 left-0 right-0 z-50 bg-coral-orange text-white text-center py-2 px-4 text-sm md:text-base font-medium transition-all duration-300 block hover:bg-coral-orange/90 ${
            bannerHidden ? 'opacity-0 pointer-events-none -translate-y-full' : 'opacity-100 translate-y-0'
          }`}
        >
          🏕️🌊 $49 Glamping Cabins Friday & $49 Cabanas ALL WEEKEND at Son's River Ranch — <span className="font-bold">BOOK NOW!</span> 🎉☀️
        </a>
      )}
      
      <header
        className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
          isBookingPage
            ? "top-0"
            : bannerHidden
              ? "top-0"
              : "top-[36px] md:top-[40px]"
        } ${
          isScrolled
            ? "bg-teal shadow-soft py-2"
            : "bg-teal py-3"
        }`}
      >
      <div className="container-narrow mx-auto px-4 md:px-8">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center group"
          >
            <img 
              src={logo} 
              alt="Son's Guadalupe - Private River Basecamp" 
              width={217}
              height={64}
              fetchPriority="high"
              className={`transition-all duration-300 drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${
                isScrolled ? "h-12 md:h-14" : "h-14 md:h-16"
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              item.dropdown ? (
                <div 
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.name)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                      location.pathname === item.path.split('#')[0]
                        ? "bg-white/20 text-white"
                        : "text-white/90 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    {item.name}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === item.name && (
                    <div className="absolute top-full left-0 pt-2">
                      <div className="bg-white rounded-lg shadow-elevated py-2 min-w-[180px] z-50">
                        {item.dropdown.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => handleNavClick(subItem.path)}
                            className="block px-4 py-2 text-sm text-charcoal hover:bg-muted transition-colors"
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? "bg-white/20 text-white"
                      : "text-white/90 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:877-577-7667"
              className="flex items-center gap-2 text-sm font-medium text-white"
            >
              <Phone className="w-4 h-4" />
              <span>877-577-7667</span>
            </a>
            <Button variant="nav" size="lg" onClick={() => { gtagReportConversion(); setBookNowMenuOpen(true); }}>
              Book Now
            </Button>
          </div>

          {/* Mobile/Tablet CTA + Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <Button 
              variant="nav" 
              size="sm" 
              onClick={() => { gtagReportConversion(); setBookNowMenuOpen(true); }}
            >
              Book Now
            </Button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 animate-fade-in">
            <div className="bg-card rounded-xl shadow-elevated p-4 space-y-2">
              {navItems.map((item) => (
                item.dropdown ? (
                  <div key={item.path} className="space-y-1">
                    <div className="px-4 py-3 text-sm font-bold text-charcoal">
                      {item.name}
                    </div>
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        onClick={() => handleNavClick(subItem.path)}
                        className="block px-6 py-2 text-sm text-charcoal/80 hover:bg-muted rounded-lg"
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === item.path
                        ? "bg-muted text-charcoal"
                        : "text-charcoal hover:bg-muted"
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              <div className="pt-4 border-t border-border space-y-3">
                <a
                  href="tel:877-577-7667"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-charcoal"
                >
                  <Phone className="w-4 h-4" />
                  <span>877-577-7667</span>
                </a>
                <Button 
                  variant="default" 
                  className="w-full" 
                  size="lg"
                  onClick={() => {
                    gtagReportConversion();
                    setIsMobileMenuOpen(false);
                    setBookNowMenuOpen(true);
                  }}
                >
                  Book Now
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>

    {/* Booking Modals */}
    <BookNowMenuModal 
      open={bookNowMenuOpen} 
      onOpenChange={setBookNowMenuOpen}
      onSelectCabana={() => setCabanaBookingOpen(true)}
      onSelectTubing={() => setTubingBookingOpen(true)}
      onSelectGlamping={() => setGlampingBookingOpen(true)}
    />
    <BookingPopupModal open={cabanaBookingOpen} onOpenChange={setCabanaBookingOpen} />
    <TubingBookingPopupModal open={tubingBookingOpen} onOpenChange={setTubingBookingOpen} />
    <GlampingBookingPopupModal open={glampingBookingOpen} onOpenChange={setGlampingBookingOpen} />
    </>
  );
}
